import React from 'react';
import { UploadCloud, X, CheckCircle2, Sparkles, Loader2 } from 'lucide-react';
import Badge from '../../components/ui/Badge';

export function FourPoseAvatarUploader({
  mode = 'static',
  onModeChange,
  staticAvatarUrl,
  onUploadStaticAvatar,
  onRemoveStaticAvatar,
  poses = {
    neutral: '',
    pointLeft: '',
    pointRight: '',
    attention: ''
  },
  onUploadPose,
  onRemovePose,
  onGeneratePoses,
  isGeneratingPoses = false
}) {
  const poseConfigs = [
    {
      key: 'neutral',
      title: 'Pose giữa / tự nhiên',
      desc: 'Ảnh đứng giữa hoặc hai tay hạ.'
    },
    {
      key: 'pointLeft',
      title: 'Pose chỉ bên trái',
      desc: 'Dùng khi nói về phương án A.'
    },
    {
      key: 'pointRight',
      title: 'Pose chỉ bên phải',
      desc: 'Dùng khi nói về phương án B.'
    },
    {
      key: 'attention',
      title: 'Pose giơ tay',
      desc: 'Dùng cho cảnh mở đầu / nhấn ý.'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Mode Selector Tabs */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          type="button"
          onClick={() => onModeChange('static')}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            background: mode === 'static' ? '#ECFDF5' : 'var(--bg-subtle)',
            border: `1.5px solid ${mode === 'static' ? '#10B981' : 'var(--border-color)'}`,
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all var(--transition-fast)'
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 700, color: mode === 'static' ? '#065F46' : 'var(--text-main)' }}>
            1 ảnh avatar tĩnh (nhanh)
          </span>
          {mode === 'static' && <CheckCircle2 size={14} color="#10B981" />}
        </button>

        <button
          type="button"
          onClick={() => onModeChange('four_poses')}
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            background: mode === 'four_poses' ? '#FFF7ED' : 'var(--bg-subtle)',
            border: `1.5px solid ${mode === 'four_poses' ? 'var(--primary)' : 'var(--border-color)'}`,
            cursor: 'pointer',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'all var(--transition-fast)'
          }}
        >
          <span style={{ fontSize: '12px', fontWeight: 700, color: mode === 'four_poses' ? '#9A3412' : 'var(--text-main)' }}>
            Bộ 4 ảnh pose cố định
          </span>
          <Badge variant="warning" size="sm">Khuyên dùng</Badge>
        </button>
      </div>

      {/* Nhờ AI vẽ các pose chỉ tay từ chính ảnh mascot đã tải lên */}
      {(staticAvatarUrl || poses.neutral) && (
        <button
          type="button"
          onClick={onGeneratePoses}
          disabled={isGeneratingPoses}
          style={{
            padding: '9px 12px',
            borderRadius: 'var(--radius-md)',
            background: isGeneratingPoses ? 'var(--bg-subtle)' : 'linear-gradient(90deg, #7C3AED 0%, #DB2777 100%)',
            border: '1px solid rgba(124, 58, 237, 0.25)',
            color: isGeneratingPoses ? 'var(--text-muted)' : '#FFFFFF',
            fontSize: '12px',
            fontWeight: 700,
            cursor: isGeneratingPoses ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '7px'
          }}
        >
          {isGeneratingPoses ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          <span>
            {isGeneratingPoses ? 'AI đang vẽ pose chỉ tay...' : 'Nhờ AI vẽ pose chỉ trái / chỉ phải / giơ tay'}
          </span>
        </button>
      )}

      {/* Mode 1: Static Single Avatar */}
      {mode === 'static' ? (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, background: '#10B981', color: '#FFFFFF', padding: '2px 6px', borderRadius: 'var(--radius-full)' }}>
              Host / Mascot
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Ảnh giữ nguyên mọi cảnh, không dùng AI vẽ lại.
            </span>
          </div>

          <div
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.onchange = (e) => e.target.files?.[0] && onUploadStaticAvatar(e.target.files[0]);
              input.click();
            }}
            style={{
              width: '100%',
              height: '90px',
              borderRadius: 'var(--radius-md)',
              background: '#FFFFFF',
              border: `1.5px dashed ${staticAvatarUrl ? '#10B981' : 'var(--border-color)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {staticAvatarUrl ? (
              <>
                <img
                  src={staticAvatarUrl}
                  alt="Static Mascot"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveStaticAvatar();
                  }}
                  style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-dim)' }}>
                <UploadCloud size={20} color="#10B981" />
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-main)' }}>
                  Kéo thả hoặc bấm để tải ảnh Mascot
                </span>
                <span style={{ fontSize: '10px' }}>(PNG transparent)</span>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Mode 2: 4 Fixed Poses */
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-main)' }}>
              Tải 4 pose theo đúng form
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Tự động đổi pose theo phân cảnh
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {poseConfigs.map((cfg) => {
              const currentPoseUrl = poses[cfg.key];

              return (
                <div
                  key={cfg.key}
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => e.target.files?.[0] && onUploadPose(cfg.key, e.target.files[0]);
                    input.click();
                  }}
                  style={{
                    background: '#FFFFFF',
                    border: `1.5px dashed ${currentPoseUrl ? 'var(--primary)' : 'var(--border-color)'}`,
                    borderRadius: 'var(--radius-sm)',
                    padding: '8px 10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    cursor: 'pointer',
                    position: 'relative',
                    minHeight: '75px',
                    justifyContent: 'center',
                    transition: 'all var(--transition-fast)'
                  }}
                >
                  {currentPoseUrl ? (
                    <div style={{ position: 'relative', width: '100%', height: '50px' }}>
                      <img
                        src={currentPoseUrl}
                        alt={cfg.title}
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePose(cfg.key);
                        }}
                        style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          background: 'rgba(0, 0, 0, 0.7)',
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--primary)' }}>
                        + {cfg.title}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default FourPoseAvatarUploader;
